// "Shop the set" (snippets/family-set.liquid, Oct 21): one row per person (garment, color, word, size), and one button
// that adds the whole set through the cart's /cart/add.js (several items in one request), then opens the cart drawer as
// the page's own Add to cart does. The deal itself is Shopify's automatic discount; this only shows it.
// Hineni's sets (Oct 21): a deal in percent (d.percent: off every piece), and a product's own first option besides Color
// and Size (Chutzpah's Word, the Immanuel bodysuit's Front) is the row's choice, named as the product names it.
// Colors (Oct 21, Matti: "even if colors are not perfectly aligned find a way to bridge that gap… top has the main color
// circle but then for each selection it is the right color name"): the circles are the set's color lines (d.lines:
// name, swatch, prefer). A line puts each person's garment in the first of its `prefer` colors the garment comes in, or
// the color nearest its swatch when it has none of them, and each row names that color in its own color line, where it
// can be changed. Without lines, the circles are this product's own colors (each its own line).
if (!customElements.get('family-set')) {
  const money = (cents) => `$${cents % 100 ? (cents / 100).toFixed(2) : cents / 100}`;
  const el = (tag, attrs = {}, ...kids) => {
    const n = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs)) if (v != null && v !== false) n.setAttribute(k, v === true ? '' : v);
    n.append(...kids);
    return n;
  };
  // colors compared as people see them: CIELAB, lightness counted half (a light grey isn't "nearer" to a mid blue than navy)
  const lab = (hex) => {
    const m = /^#?([0-9a-f]{6})$/i.exec(String(hex ?? '').trim());
    if (!m) return null;
    const [r, g, b] = [0, 2, 4].map((i) => parseInt(m[1].slice(i, i + 2), 16) / 255).map((c) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
    const f = (t) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
    const x = f((0.4124 * r + 0.3576 * g + 0.1805 * b) / 0.95047), y = f(0.2126 * r + 0.7152 * g + 0.0722 * b), z = f((0.0193 * r + 0.1192 * g + 0.9505 * b) / 1.08883);
    return [116 * y - 16, 500 * (x - y), 200 * (y - z)];
  };
  const apart = (a, b) => Math.hypot((a[0] - b[0]) / 2, a[1] - b[1], a[2] - b[2]);
  // true once the picture has fully loaded, false if it failed or hasn't within 4 s (the picture then isn't swapped in)
  const loaded = (img) => (img.complete && img.naturalWidth ? Promise.resolve(true) : new Promise((done) => {
    img.addEventListener('load', () => done(true), { once: true });
    img.addEventListener('error', () => done(false), { once: true });
    setTimeout(() => done(img.complete && img.naturalWidth > 0), 4000);
  }));

  customElements.define('family-set', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      const d = JSON.parse(this.querySelector('[data-fset-data]').textContent);
      // short garment names: the titles less the words they share ("Nes Gadol Haya Po Youth Tee" → "Youth Tee"), and
      // less Hineni's English in brackets ("Hineni Youth Tee (Here I Am)" → "Youth Tee"); a grown-up's garment says so
      // ("Family Tee" → "Adult Tee", Hineni's "Long Sleeve" → "Adult Long Sleeve")
      const titles = d.products.map((p) => p.title.replace(/\s*\([^)]*\)\s*$/, ''));
      let pre = titles[0];
      for (const t of titles) while (!t.startsWith(pre)) pre = pre.slice(0, pre.lastIndexOf(' ', pre.length - 2) + 1);
      for (const [i, p] of d.products.entries()) {
        // option values as names, whether Liquid's json gave strings or value objects
        for (const k of Object.keys(p.values)) p.values[k] = p.values[k].map((x) => (x && typeof x === 'object' ? x.name ?? x.value : x));
        p.short = (titles[i].slice(pre.length) || titles[i]).replace(/^Family /, '');
        if (!/youth|toddler|baby|kid|infant/i.test(p.short)) p.short = `Adult ${p.short}`;
        const choice = p.options.find((o) => !['Color', 'Size', 'Style'].includes(o));
        p.choiceName = choice ?? null;
        p.at = { Word: choice ? p.options.indexOf(choice) : -1, Color: p.options.indexOf('Color'), Size: p.options.indexOf('Size') };
        p.values.Word = choice ? p.values[choice] : undefined;
      }
      this.d = d;
      const here = this.product(this.dataset.handle);
      // the circles: the set's lines, else this product's own colors
      this.lines = d.lines?.length ? d.lines : (here.values.Color ?? []).map((c) => ({ name: c, swatch: here.swatches?.[c], prefer: [c] }));
      // the page's color picks its line: the first line that lists it and dresses this product in it, else the first line
      // (the Hineni Tee opens on Espresso, which no line lists: nearest would have put the parents in Brick)
      this.line = this.lines.find((l) => (l.prefer ?? [l.name]).includes(d.page) && this.colorFor(here, l) === d.page)?.name ?? this.lines[0]?.name;
      this.rows = (d.rows.length ? d.rows : [{ handle: d.products[0].handle, word: '' }]).map((r) => ({ handle: r.handle, word: r.word ?? '', size: '' }));
      // on a garment the starting rows leave out, each row takes its age's twin of this garment where there is one (a
      // sweatshirt page: grown-ups', youth and toddler sweatshirts), else this garment takes the last row (the baby bodysuit)
      if (!this.rows.some((r) => r.handle === here.handle) && d.products.some((p) => p.handle === this.dataset.handle)) {
        const kind = here.short.replace(/^\S+\s/, '');
        for (const r of this.rows) {
          const twin = d.products.find((p) => p.short === `${this.product(r.handle).short.split(' ')[0]} ${kind}`);
          if (twin) r.handle = twin.handle;
        }
        if (!this.rows.some((r) => r.handle === here.handle)) this.rows[this.rows.length - 1].handle = here.handle;
      }
      for (const r of this.rows) {
        // a row on a product with a choice (Word, Front) starts on its first value when the rows don't name one
        const p = this.product(r.handle), w = p.values.Word;
        if (w && !w.includes(r.word)) r.word = w[0];
        r.color = this.colorFor(p, this.currentLine());
      }
      this.list = this.querySelector('[data-fset-rows]');
      this.sum = this.querySelector('[data-fset-sum]');
      this.msg = this.querySelector('[data-fset-msg]');
      this.querySelector('[data-fset-more]').addEventListener('click', () => {
        const last = this.rows[this.rows.length - 1], words = this.product(last.handle).values.Word ?? [];
        this.rows.push({ handle: last.handle, word: words[(words.indexOf(last.word) + 1) % words.length] ?? '', color: last.color, size: '' });
        this.render();
        this.list.lastElementChild?.querySelector('select')?.focus();
      });
      this.querySelectorAll('[data-fset-color]').forEach((b) => b.addEventListener('click', () => this.setLine(b.dataset.fsetColor)));
      // what the family wears (Oct 21, Matti: "on the top maybe have t-shirts or sweatshirts option so people can see both
      // easily"): one button per line-up, named for its garment (the line-up's first product less its age word: "Adult
      // Sweatshirt" → Sweatshirts); a tap dresses every person whose age has that garment in it and shows that family
      this.kinds = (d.lineups ?? []).map((handles) => this.product(handles[0])).filter((p, i, all) => p && all.indexOf(p) === i).map((p) => {
        const word = p.short.replace(/^\S+\s/, '');
        return { word, label: /^(Tee|T-Shirt)$/.test(word) ? 'T-shirts' : `${word.replace(/ Sleeve$/, ' sleeve')}s` };
      });
      const kindBox = this.querySelector('[data-fset-kinds]');
      if (kindBox && this.kinds.length > 1) {
        kindBox.replaceChildren(...this.kinds.map((k, i) => {
          const b = el('button', { type: 'button', class: 'fset__kind', 'aria-pressed': 'false', 'data-fset-kind': i }, k.label);
          b.addEventListener('click', () => this.setKind(i));
          return b;
        }));
        kindBox.hidden = false;
      }
      this.querySelector('[data-fset-add]').addEventListener('click', () => this.add());
      this.render();
    }

    // No family pictures in turn any more (Oct 22, Matti: "don't do the changing image from the tshirts to the sweatshirt
    // on it's own. it might confuse people"; they switched every 4.5 s since Oct 21): the page's own line-up shows, and
    // the T-shirts / Sweatshirts buttons show the other.
    // the gallery's family photo, when it is the one showing and in this product's color for the line: the next picture
    // is loaded out of sight at the photo's own size, then the photo, its thumbnail and the lightbox copy take it at once
    showBig(pic) {
      const color = this.colorFor(this.product(this.dataset.handle), this.currentLine());
      const gallery = (this.closest('product-info') ?? document).querySelector('media-gallery');
      const img = [...(gallery?.querySelectorAll('li[data-media-id].is-active img') ?? [])].find((i) => i.alt.includes(`, ${color}, the whole family`));
      if (!img || img.getAttribute('srcset') === pic.getAttribute('srcset')) return;
      const probe = new Image();
      probe.sizes = img.sizes || `${img.clientWidth || 600}px`;
      probe.srcset = pic.getAttribute('srcset') ?? '';
      probe.src = pic.getAttribute('src');
      loaded(probe).then((ok) => { if (ok) this.paint(color, pic); });
    }
    // every copy of this product's family photo in that color outside the box (photo, thumbnail, lightbox) takes a
    // picture; the alt stays, so later lookups still match
    paint(color, pic) {
      document.querySelectorAll('img').forEach((img) => {
        if (!this.contains(img) && img.alt.includes(`, ${color}, the whole family`) && img.getAttribute('srcset') !== pic.getAttribute('srcset')) {
          img.setAttribute('srcset', pic.getAttribute('srcset'));
          img.setAttribute('src', pic.getAttribute('src'));
        }
      });
    }

    product(handle) { return this.d.products.find((p) => p.handle === handle) ?? this.d.products[0]; }
    currentLine() { return this.lines.find((l) => l.name === this.line) ?? this.lines[0]; }

    // the color a line gives a garment: the first of its `prefer` colors the garment comes in, else the nearest to its swatch
    colorFor(p, line) {
      const colors = p.values.Color ?? [];
      if (!line || !colors.length) return colors[0] ?? '';
      return (line.prefer ?? [line.name]).find((c) => colors.includes(c)) ?? this.nearest(p, line.swatch) ?? colors[0];
    }
    // the garment's color nearest a swatch
    nearest(p, hex) {
      const to = lab(hex);
      if (!to) return null;
      let best = null, gap = Infinity;
      for (const c of p.values.Color ?? []) {
        const at = lab(p.swatches?.[c]);
        if (at && apart(at, to) < gap) { gap = apart(at, to); best = c; }
      }
      return best;
    }

    // the row's variant in its color (any size until one is chosen, for its price)
    variant(r) {
      const p = this.product(r.handle), at = p.at;
      const fits = (v, withSize) => (at.Word < 0 || v.o[at.Word] === r.word) && (at.Color < 0 || v.o[at.Color] === r.color) && (!withSize || at.Size < 0 || v.o[at.Size] === r.size);
      return r.size || at.Size < 0 ? p.variants.find((v) => fits(v, true)) : p.variants.find((v) => fits(v, false));
    }

    // a product's garment among the kinds (its short name less the age word), or -1
    kindOf(p) { return (this.kinds ?? []).findIndex((k) => p.short.replace(/^\S+\s/, '') === k.word); }
    // the same age's product in a kind ("Youth Tee" → "Youth Sweatshirt"), if the set has one
    twinIn(p, k) { return this.d.products.find((q) => q.short === `${p.short.split(' ')[0]} ${this.kinds[k].word}`); }

    // a T-shirts / Sweatshirts button: every person whose age has that garment wears it (the baby keeps the bodysuit),
    // in the line's color, word and size kept where they fit; that family shows, here and in the gallery
    setKind(k) {
      this.kind = k;
      for (const r of this.rows) {
        const twin = this.twinIn(this.product(r.handle), k);
        if (!twin || twin.handle === r.handle) continue;
        r.handle = twin.handle;
        if (!(twin.values.Word ?? []).includes(r.word)) r.word = (twin.values.Word ?? [''])[0];
        r.color = this.colorFor(twin, this.currentLine());
        if (!(twin.values.Size ?? []).includes(r.size)) r.size = '';
      }
      this.render();
      const pic = this.linePictures()[0];
      if (pic) this.showBig(pic);
    }

    // a circle: everyone in that line's color for their garment
    setLine(name) {
      this.line = name;
      for (const r of this.rows) r.color = this.colorFor(this.product(r.handle), this.currentLine());
      this.render();
      this.showOnPage();
    }

    // the page follows a circle (Matti, Oct 21: "can you make the picture on the left change to the selected color too?
    // like the small image?"): this product's own color for the line is picked in the page's picker (the one-shirt
    // section below then matches), and once Dawn has swapped in that color's photos (variantChange; the other colors'
    // photos aren't in the page, media-hidden.liquid) the gallery opens on its family picture, where it has one
    // The page stays where it is (Matti: a tap on a circle jumped down to the one-shirt picker): Dawn focuses the picker
    // input it changed, which scrolls to it, and on phones the gallery scrolls up to its photo; both are undone once the
    // gallery's own timer has run, and the circle keeps the focus.
    showOnPage() {
      const color = this.colorFor(this.product(this.dataset.handle), this.currentLine());
      const page = this.closest('product-info') ?? document;
      const y = window.scrollY, from = document.activeElement;
      // the gallery's smooth scroll up to its photo (phones) starts after this check could see it, so it is held instead
      // (media-gallery.js), for the variant change and the family photo below
      window.galleryScrollHold = Date.now() + 3000;
      const stay = () => {
        if (from instanceof HTMLElement && from.isConnected && document.activeElement !== from) from.focus({ preventScroll: true });
        if (Math.abs(window.scrollY - y) > 2) window.scrollTo({ top: y, behavior: 'instant' });
      };
      // Dawn's focus scrolls inside focus() itself: taking the focus back to the circle during its focusin stops that
      // scroll (Chrome scrolls only an element still focused), and a microtask puts the page back before it paints if
      // a browser scrolled anyway. A hidden tab fires no focus events: the timer after the gallery covers it.
      const onFocus = (event) => { if (event.target.closest?.('variant-selects')) { stay(); queueMicrotask(stay); } };
      document.addEventListener('focusin', onFocus);
      setTimeout(() => document.removeEventListener('focusin', onFocus), 4000);
      const show = () => {
        const gallery = (this.closest('product-info') ?? document).querySelector('media-gallery');
        const item = [...(gallery?.querySelectorAll('[data-media-id]') ?? [])].find((li) => [...li.querySelectorAll('img')].some((img) => img.alt.includes(`, ${color}, the whole family`)));
        if (item) {
          // the line's own picture in place of this product's (the toddler tee's Black family under Navy: the Navy family,
          // the toddler in Black): its photo, thumbnail and the lightbox copy; the alt stays, so later lookups still match
          const pic = this.linePictures()[0];
          if (pic) this.paint(color, pic);
          gallery.setActiveMedia?.(item.dataset.mediaId, true);
        }
        setTimeout(stay, 0); // after setActiveMedia's own setTimeout
      };
      const input = [...page.querySelectorAll('variant-selects input[data-option-name="Color"]')].find((i) => i.value === color);
      if (!input || input.checked) return show();
      // Dawn's PUB_SUB_EVENTS is a script-level const, not a window property
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        const off = subscribe(PUB_SUB_EVENTS.variantChange, () => { off(); setTimeout(show, 0); });
      }
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // the circles and the family picture follow the line: the picture is the one its products wear in that line
    syncLine() {
      this.querySelectorAll('[data-fset-color]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.fsetColor === this.line)));
      this.querySelector('[data-fset-color-label]').textContent = `Color: ${this.line ?? ''}`;
      // the garment every person who has a choice wears (a mixed family presses neither)
      const worn = new Set(this.rows.map((r) => this.kindOf(this.product(r.handle))).filter((k) => k >= 0));
      this.querySelectorAll('[data-fset-kind]').forEach((b) => b.setAttribute('aria-pressed', String(worn.size === 1 && worn.has(Number(b.dataset.fsetKind)))));
      const imgs = [...this.querySelectorAll('[data-fset-photo]')], pic = this.linePictures()[0];
      // a line with no picture keeps the one showing (or the first)
      if (pic) imgs.forEach((img) => { img.hidden = img !== pic; });
      else if (imgs.length && imgs.every((img) => img.hidden)) imgs[0].hidden = false;
    }

    // the family picture drawn for the current line (Oct 21, Matti: "change what can change so people can know"): a
    // product's picture in its color for the line is that line's own when no earlier line gives the product that color
    // (set-photo.mjs writes the first line's); looked for on this product, its twins (a sweatshirt page: the sweatshirts'
    // line-up), then the rest of the set; else this product's picture in its color
    // one picture per line-up (d.lineups: the products in each family picture) in the current line, this product's own
    // line-up first; a product in an earlier line-up (the baby, in both) carries that one's picture, so a later line-up
    // looks only at its own products. Without line-ups, linePicture() alone.
    linePictures() {
      const line = this.currentLine(), here = this.product(this.dataset.handle), imgs = [...this.querySelectorAll('[data-fset-photo]')];
      const of = (p, c) => imgs.find((img) => img.dataset.fsetPhotoOf === p.handle && img.dataset.fsetPhoto === c);
      const seen = new Set(), pics = [];
      for (const handles of this.d.lineups ?? []) {
        const own = handles.filter((h) => !seen.has(h));
        handles.forEach((h) => seen.add(h));
        const products = own.map((h) => this.d.products.find((p) => p.handle === h)).filter(Boolean).sort((a, b) => (b === here) - (a === here));
        const pic = products.map((p) => { const c = this.colorFor(p, line), img = of(p, c); return img && this.lines.find((l) => this.colorFor(p, l) === c) === line ? img : null; }).find(Boolean);
        if (pic) pics[(this.kind != null ? this.kindOf(this.product(handles[0])) === this.kind : products.includes(here)) ? 'unshift' : 'push'](pic);
      }
      const mine = this.linePicture();
      return pics.length ? pics : mine ? [mine] : [];
    }

    linePicture() {
      const line = this.currentLine(), imgs = [...this.querySelectorAll('[data-fset-photo]')];
      const of = (p, c) => imgs.find((img) => img.dataset.fsetPhotoOf === p.handle && img.dataset.fsetPhoto === c);
      const here = this.product(this.dataset.handle), kind = here.short.replace(/^\S+\s/, '');
      const order = [here, ...this.d.products.filter((p) => p !== here && p.short.endsWith(kind)), ...this.d.products.filter((p) => p !== here && !p.short.endsWith(kind))];
      for (const p of order) {
        const c = this.colorFor(p, line), img = of(p, c);
        if (img && this.lines.find((l) => this.colorFor(p, l) === c) === line) return img;
      }
      return of(here, this.colorFor(here, line)) ?? null;
    }

    render() {
      this.list.replaceChildren(...this.rows.map((r, i) => {
        const p = this.product(r.handle), n = i + 1;
        const select = (key, label, values, value, placeholder) => {
          const s = el('select', { class: 'fset__select', 'aria-label': `${label}, person ${n}`, 'data-k': key },
            ...(placeholder ? [el('option', { value: '', disabled: true, selected: !value }, placeholder)] : []),
            ...values.map(([v, text]) => el('option', { value: v, selected: v === value }, text)));
          s.addEventListener('change', () => {
            const was = { color: r.color, swatch: p.swatches?.[r.color] };
            r[key] = s.value;
            if (key === 'handle') { // a new garment: keep the word, color and size where it has them
              const q = this.product(r.handle);
              if (!(q.values.Word ?? []).includes(r.word)) r.word = (q.values.Word ?? [''])[0];
              // the same color by name, else the nearest to the one the person had
              if (!(q.values.Color ?? []).includes(was.color)) r.color = this.nearest(q, was.swatch) ?? this.colorFor(q, this.currentLine());
              if (!(q.values.Size ?? []).includes(r.size)) r.size = '';
            }
            this.render();
            this.list.children[i]?.querySelector(`[data-k="${key}"]`)?.focus();
          });
          return s;
        };
        const v = this.variant(r);
        const row = el('li', { class: `fset__row${r.bad ? ' is-bad' : ''}` },
          el('span', { class: 'fset__n', 'aria-hidden': 'true' }, String(n)),
          select('handle', 'Garment', this.d.products.map((x) => [x.handle, x.short]), r.handle),
          // the person's color by name, its dot beside it
          ...(p.values.Color ? [el('span', { class: 'fset__pick', style: `--sw: ${p.swatches?.[r.color] ?? 'transparent'}` }, select('color', 'Color', p.values.Color.map((c) => [c, c]), r.color))] : []),
          ...(p.values.Word ? [select('word', p.choiceName, p.values.Word.map((w) => [w, w]), r.word)] : []),
          ...(p.values.Size ? [select('size', 'Size', p.values.Size.map((s) => [s, s]), r.size, 'Size')] : []),
          el('span', { class: 'fset__price' }, v ? money(v.p) : ''),
          this.rows.length > 1 ? (() => {
            const x = el('button', { type: 'button', class: 'fset__x', 'aria-label': `Remove person ${n}` }, '×');
            x.addEventListener('click', () => { this.rows.splice(i, 1); this.render(); });
            return x;
          })() : el('span'));
        return row;
      }));
      // the total, and the deal once the set reaches it
      // a percent deal comes off every piece (Shopify rounds each line; this total can differ from checkout by a cent)
      const count = this.rows.length, sub = this.rows.reduce((t, r) => t + (this.variant(r)?.p ?? 0), 0);
      const off = count < this.d.min ? 0 : this.d.percent ? this.rows.reduce((t, r) => t + Math.round(((this.variant(r)?.p ?? 0) * this.d.percent) / 100), 0) : this.d.amount;
      const dealText = this.d.percent ? `${this.d.percent}% off every piece` : `${money(this.d.amount)} off`;
      const line = (a, b, cls = '') => el('p', { class: `fset__line ${cls}` }, el('span', {}, a), el('span', {}, b));
      this.sum.replaceChildren(
        line(`${count} ${count === 1 ? 'piece' : 'pieces'}`, money(sub)),
        off ? line(this.d.percent ? `Family set, ${this.d.percent}% off` : 'Family set', `−${money(off)}`, 'fset__line--off') : el('p', { class: 'fset__hint' }, `Add ${this.d.min - count} more for ${dealText}.`),
        line('Total', money(sub - off), 'fset__line--total'));
      this.syncLine();
    }

    async add() {
      const missing = this.rows.filter((r) => this.product(r.handle).values.Size && !r.size);
      this.rows.forEach((r) => { r.bad = missing.includes(r); });
      if (missing.length) {
        this.render();
        this.msg.textContent = 'Choose a size for each person.';
        this.list.querySelector('.is-bad [data-k="size"]')?.focus();
        return;
      }
      const ids = new Map();
      for (const r of this.rows) {
        const v = this.variant(r);
        if (!v || !v.a) { this.msg.textContent = `${[this.product(r.handle).short, r.word, r.color, r.size].filter(Boolean).join(', ')} is sold out.`; return; }
        ids.set(v.id, (ids.get(v.id) ?? 0) + 1);
      }
      const btn = this.querySelector('[data-fset-add]'), label = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding…';
      this.msg.textContent = '';
      const cart = document.querySelector('cart-drawer') ?? document.querySelector('cart-notification');
      try {
        const res = await fetch(`${window.routes?.cart_add_url ?? '/cart/add'}.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [...ids].map(([id, quantity]) => ({ id, quantity })), ...(cart && { sections: cart.getSectionsToRender().map((s) => s.id), sections_url: window.location.pathname }) }),
        });
        const json = await res.json();
        if (!res.ok || json.status) throw new Error(json.description || json.message || 'Something went wrong');
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'family-set', cartData: json });
        // as Dawn's product-form.js: a drawer drawn for an empty cart hides its item list until is-empty goes
        if (cart?.tagName === 'CART-DRAWER') { cart.classList.remove('is-empty'); cart.renderContents({ id: json.items?.[0]?.id, sections: json.sections }); }
        else window.location = window.routes?.cart_url ?? '/cart';
        this.msg.textContent = `Added ${this.rows.length} pieces to your cart.`;
      } catch (err) {
        this.msg.textContent = err.message;
      } finally {
        btn.disabled = false;
        btn.textContent = label;
      }
    }
  });
}
